import { useEffect, useMemo, useState } from 'react';
import { Download, Printer, RefreshCw, RotateCcw } from 'lucide-react';
import { Badge, Button, Drawer, Page, Panel, Toast } from '../../admin-ui';
import { api } from '../../services/api';
import { formatMoney } from '../../lib/billing';

function today() {
  return new Date().toLocaleDateString('en-CA');
}

function token() {
  return localStorage.getItem('neon_token') || localStorage.getItem('token') || '';
}

function statusVariant(status) {
  if (status === 'issued') return 'success';
  if (status === 'cancelled') return 'danger';
  return 'neutral';
}

export default function AdminInvoices() {
  const [filters, setFilters] = useState({ from: today(), to: today(), series: '', status: '' });
  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const query = useMemo(() => new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString(), [filters]);

  const load = async () => {
    setLoading(true);
    try {
      setData(await api.get(`/invoices?${query}`));
    } catch (err) {
      setToast({ variant: 'danger', message: err.message || 'No se pudieron cargar facturas' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [query]);

  const cancelInvoice = async (invoice) => {
    const reason = window.prompt(`Motivo de anulación de ${invoice.fullNumber}`);
    if (!reason) return;
    try {
      await api.post(`/invoices/${invoice._id}/cancel`, { reason });
      setToast({ variant: 'success', message: 'Factura anulada' });
      setSelected(null);
      await load();
    } catch (err) {
      setToast({ variant: 'danger', message: err.message || 'No se pudo anular' });
    }
  };

  const downloadPdf = async (invoice) => {
    const res = await fetch(`/api/invoices/${invoice._id}/pdf`, { headers: { Authorization: `Bearer ${token()}` } });
    if (!res.ok) throw new Error('No se pudo descargar el PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.fullNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Page
      title="Facturas"
      description="Tickets simplificados, facturas completas, cobros y anulaciones"
      actions={<Button variant="secondary" iconLeft={<RefreshCw size={16} />} loading={loading} onClick={load}>Actualizar</Button>}
    >
      <Toast {...toast} onClose={() => setToast(null)} />
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)', gap: '1rem' }}>
        <Panel title="Filtros">
          <div style={{ display: 'grid', gap: '.65rem' }}>
            {[
              ['from', 'Desde', 'date'],
              ['to', 'Hasta', 'date']
            ].map(([key, label, type]) => (
              <label key={key} style={{ display: 'grid', gap: '.25rem', color: 'var(--a-muted)', fontSize: '.85rem' }}>
                {label}
                <input type={type} value={filters[key]} onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))} />
              </label>
            ))}
            <label style={{ display: 'grid', gap: '.25rem', color: 'var(--a-muted)', fontSize: '.85rem' }}>
              Serie
              <select value={filters.series} onChange={(e) => setFilters((prev) => ({ ...prev, series: e.target.value }))}>
                <option value="">Todas</option>
                <option value="T">Ticket T</option>
                <option value="F">Factura F</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: '.25rem', color: 'var(--a-muted)', fontSize: '.85rem' }}>
              Estado
              <select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
                <option value="">Todos</option>
                <option value="issued">Emitidas</option>
                <option value="cancelled">Anuladas</option>
              </select>
            </label>
          </div>
        </Panel>

        <Panel
          title={`${data.summary?.count || 0} documentos · ${formatMoney(data.summary?.totalGross || 0)} bruto · ${formatMoney(data.summary?.totalTax || 0)} IVA`}
        >
          <div style={{ overflowX: 'auto' }}>
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nº</th>
                  <th>Mesa</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>IVA</th>
                  <th>Método</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((invoice) => (
                  <tr key={invoice._id} onClick={() => setSelected(invoice)}>
                    <td>{new Date(invoice.date).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td><strong>{invoice.fullNumber}</strong></td>
                    <td>M{invoice.tableSnapshot?.number || '?'}</td>
                    <td>{invoice.items?.length || 0}</td>
                    <td>{formatMoney(invoice.total)}</td>
                    <td>{formatMoney(invoice.taxAmount)}</td>
                    <td>{invoice.paymentMethodLabel || invoice.paymentMethod}</td>
                    <td><Badge variant={statusVariant(invoice.status)}>{invoice.status === 'issued' ? 'Emitida' : 'Anulada'}</Badge></td>
                  </tr>
                ))}
                {!data.items.length && (
                  <tr><td colSpan="8" style={{ color: 'var(--a-muted)', textAlign: 'center', padding: '1.5rem' }}>Sin facturas en este rango.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Drawer isOpen={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.fullNumber || 'Factura'}>
        {selected && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <Panel title="Detalle">
              <div style={{ display: 'grid', gap: '.35rem' }}>
                <span>Mesa {selected.tableSnapshot?.number || '?'}</span>
                <strong style={{ fontSize: '1.7rem', color: 'var(--a-cyan)' }}>{formatMoney(selected.total)}</strong>
                <span style={{ color: 'var(--a-muted)' }}>Base {formatMoney(selected.subtotal)} · IVA {formatMoney(selected.taxAmount)}</span>
              </div>
            </Panel>
            <Panel title="Items">
              <div style={{ display: 'grid', gap: '.5rem' }}>
                {selected.items?.map((item, index) => (
                  <div key={`${item.orderId}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '.75rem', borderBottom: '1px solid var(--a-border)', paddingBottom: '.45rem' }}>
                    <span>{item.quantity}x {item.nameSnapshot}</span>
                    <strong>{formatMoney(item.total)}</strong>
                  </div>
                ))}
              </div>
            </Panel>
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <Button variant="secondary" iconLeft={<Printer size={16} />} onClick={() => window.open(`/admin/facturas/${selected._id}/print`, '_blank', 'noopener,noreferrer')}>Imprimir</Button>
              <Button variant="secondary" iconLeft={<Download size={16} />} onClick={() => downloadPdf(selected).catch((err) => setToast({ variant: 'danger', message: err.message }))}>PDF</Button>
              {selected.status === 'issued' && <Button variant="danger" iconLeft={<RotateCcw size={16} />} onClick={() => cancelInvoice(selected)}>Anular</Button>}
            </div>
          </div>
        )}
      </Drawer>
    </Page>
  );
}
